import React, { useContext, useState } from 'react';

// components
import ToggleInput from '../../../components/inputs/toggleInput';
import LoadingSpinner from '../../../components/nav/LoadingSpinner';
import styled from 'styled-components';

// theme
import aampeTheme from '../../../constants/aampeTheme';

// context
import { EditorContext } from '../../../context/EditorContext';

import useFormulaUpdateBaseMutation from '../../../hooks/formulas/useFormulaUpdateBaseMutation';
// hooks
import useIsCustomerIOPushProvider from '../../../hooks/formulas/useIsCustomerIOPushProvider';

interface CustomerIOPushSettingsProps {
    sendToUnsubscribed: boolean;
    isDisabled?: boolean;
}

const CustomerIOPushSettings: React.FC<
    React.PropsWithChildren<CustomerIOPushSettingsProps>
> = ({ sendToUnsubscribed, isDisabled }) => {
    const [isSendToUnsubscribed, setIsSendToUnsubscribed] =
        useState<boolean>(sendToUnsubscribed);

    const { editorDetails } = useContext(EditorContext);
    const { formula } = editorDetails;

    const { isLoading, isCustomerIOPushProvider } =
        useIsCustomerIOPushProvider();

    const updateFormulaMutation = useFormulaUpdateBaseMutation(
        formula?.id as number,
    );

    const onSendToUnsubscribedChanged = () => {
        setIsSendToUnsubscribed((state) => !state);
        updateFormulaMutation.mutate(
            {
                id: formula?.id as number,
                send_to_unsubscribed: !isSendToUnsubscribed,
            },
            {
                onError: () => {
                    setIsSendToUnsubscribed(sendToUnsubscribed);
                },
            },
        );
    };

    if (isLoading) {
        return <LoadingSpinner />;
    }

    if (!isCustomerIOPushProvider) {
        return null;
    }

    return (
        <Container>
            <TitleDescription>
                <Title>Unsubscribed users</Title>
                <Description>
                    By default, Aampe respects unsubscribe preferences and will
                    not send messages to unsubscribed users. Enable this option
                    to allow sending to unsubscribed users for this message
                    group.
                </Description>
            </TitleDescription>
            <Toggle>
                <Description>Send to unsubscribed users</Description>
                <ToggleInput
                    isActive={isSendToUnsubscribed}
                    toggleIsActive={onSendToUnsubscribedChanged}
                    isLoading={false}
                    isError={false}
                    disabled={isDisabled}
                />
            </Toggle>
        </Container>
    );
};

export default CustomerIOPushSettings;

const Container = styled.div({
    border: `1px solid ${aampeTheme.colors.bordersWG01}`,
    borderRadius: aampeTheme.borderRadius.default,
    padding: aampeTheme.spacing4(6),
    fontSize: aampeTheme.size.font.default,
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing4(6),
});

const TitleDescription = styled.div({
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing4(2),
});

const Title = styled.div({
    lineHeight: aampeTheme.size.lineHeight.tall,
    fontWeight: aampeTheme.weights.medium,
});

const Description = styled.div({
    lineHeight: aampeTheme.size.lineHeight.default,
    color: aampeTheme.colors.neutral09,
    fontWeight: aampeTheme.weights.light,
});

const Toggle = styled.div({
    display: 'flex',
    gap: aampeTheme.spacing4(6),
    alignItems: 'center',
});
